import type { ReactNode } from "react";
import { LegalPage } from "../components/LegalPage";
import {
  getAddressLines,
  getProviderName,
  hasIncompleteLegalProfile,
  legalProfile,
  missingLegalChecklist,
} from "../legal";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-4">
      <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
      <div className="space-y-3 text-base leading-relaxed text-gray-700">{children}</div>
    </section>
  );
}

export function Datenschutz() {
  return (
    <LegalPage
      title="Datenschutzerklärung"
      intro={
        <>
          <p>
            Diese Datenschutzerklärung informiert über Art, Umfang und Zweck der Verarbeitung
            personenbezogener Daten im Zusammenhang mit dem Internetauftritt von {legalProfile.brandName}.
          </p>
          <p>Stand: {legalProfile.lastUpdated}</p>
        </>
      }
      notice={
        hasIncompleteLegalProfile ? (
          <p>
            Für eine belastbare Veröffentlichung müssen noch Stammdaten ergänzt werden:
            {" "}
            {missingLegalChecklist.join(", ")}.
          </p>
        ) : null
      }
    >
      <Section title="1. Verantwortlicher">
        <p>
          Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) ist:
        </p>
        <div className="rounded-2xl border border-stone-200 bg-stone-50 p-5">
          <p>{getProviderName()}</p>
          {getAddressLines().map((line) => (
            <p key={line}>{line}</p>
          ))}
          <p>{legalProfile.email}</p>
          <p>{legalProfile.phone}</p>
        </div>
      </Section>

      <Section title="2. Hosting und technische Bereitstellung">
        <p>
          Beim Aufruf dieser Website werden technisch erforderliche Verbindungs- und
          Zugriffsdaten verarbeitet, damit die Seiten ausgeliefert, die Sicherheit der Systeme
          gewährleistet und Missbrauch erkannt werden kann. Hierzu können insbesondere IP-Adresse,
          Datum und Uhrzeit des Abrufs, angeforderte URL, Browser- und Geräteinformationen sowie
          Statusdaten der Auslieferung gehören.
        </p>
        <p>
          Die Verarbeitung erfolgt zur Bereitstellung der Website und zur Gewährleistung von
          Stabilität und Sicherheit auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.
        </p>
      </Section>

      <Section title="3. Kontaktanfragen und Fragebogen">
        <p>
          Wenn Sie den Fragebogen oder sonstige Kontaktmöglichkeiten nutzen, verarbeiten wir die
          von Ihnen eingegebenen Daten zur Bearbeitung Ihrer Anfrage und zur Vorbereitung eines
          möglichen Vertragsabschlusses. Dazu gehören insbesondere Name, E-Mail-Adresse,
          Telefonnummer, Firmenname sowie die von Ihnen übermittelten Projekt-, Inhalts- und
          Anforderungsdaten.
        </p>
        <p>
          Die Angaben werden serverseitig gespeichert, einem Projekt zugeordnet und für interne
          Bearbeitungsprozesse sowie zur E-Mail-Kommunikation verwendet. Rechtsgrundlage ist
          Art. 6 Abs. 1 lit. b DSGVO, bei allgemeinen Anfragen ergänzend Art. 6 Abs. 1 lit. f DSGVO.
        </p>
      </Section>

      <Section title="4. Kundenkonto und Authentifizierung">
        <p>
          Im Zusammenhang mit einem Auftrag oder dem Zugang zum Kundenbereich verarbeiten wir
          Bestands- und Zugangsdaten wie Name, E-Mail-Adresse, Unternehmensangaben,
          Passwort-Hash, Rollen- und Statusinformationen. Diese Daten werden benötigt, um
          Kundenkonten bereitzustellen, den Zugriff zu schützen und vertragsbezogene Leistungen
          bereitzustellen.
        </p>
        <p>
          Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO sowie Art. 6 Abs. 1 lit. f DSGVO für den
          Schutz des Kundenbereichs und die Missbrauchsprävention.
        </p>
      </Section>

      <Section title="5. Geschützte Entwurfsansichten und Bestellprozess">
        <p>
          Für passwortgeschützte Entwurfsansichten wird ein technisches Zugriffstoken verwendet,
          damit freigegebene Entwürfe nur nach erfolgreicher Authentifizierung angezeigt werden.
          Im Zusammenhang mit dem Bestellprozess werden außerdem Vertrags- und Bestelldaten wie
          Projektbezug, Name, E-Mail-Adresse, Zeitpunkt des Vertragsschlusses und die im Checkout
          dokumentierten Zustimmungserklärungen verarbeitet. Dabei kann auch die IP-Adresse zur
          Dokumentation des Vertragsschlusses verarbeitet werden.
        </p>
        <p>
          Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO sowie Art. 6 Abs. 1 lit. f DSGVO für die
          Absicherung nicht öffentlicher Inhalte und die Nachweisbarkeit geschäftlicher Vorgänge.
        </p>
      </Section>

      <Section title="6. Zahlungsabwicklung mit Stripe">
        <p>
          Für die Zahlungsabwicklung wird ein gehosteter Checkout von Stripe eingesetzt. Bei einem
          Wechsel in den Checkout werden die für die Zahlung und Rechnungsstellung erforderlichen
          Daten direkt durch Stripe erfasst. Dazu können insbesondere Name, E-Mail-Adresse,
          Rechnungsanschrift, Telefonnummer, Zahlungsdaten, Steuer-/USt-ID, Rabattcodes,
          Zahlungsstatus sowie transaktionsbezogene Metadaten gehören.
        </p>
        <p>
          Nach aktuellem technischen Stand sind im Checkout Kartenzahlung, PayPal, SEPA-Lastschrift
          und Klarna aktiviert. Stripe kann außerdem die Speicherung eines Zahlungsmittels für
          Folgebelastungen anbieten. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO; handels- und
          steuerrechtliche Aufbewahrungspflichten beruhen auf Art. 6 Abs. 1 lit. c DSGVO.
        </p>
        <p>
          Anbieter ist Stripe. Es kann nicht ausgeschlossen werden, dass Stripe Daten auch in
          Drittländern, insbesondere in den USA, verarbeitet. Stripe gibt an, hierfür geeignete
          Datenschutzgarantien wie Standardvertragsklauseln und weitere Transfermechanismen
          einzusetzen. Maßgeblich sind die Datenschutzinformationen von Stripe:
          {" "}
          <a
            className="underline decoration-stone-300 underline-offset-4"
            href="https://stripe.com/en-gi/legal/privacy-center"
            target="_blank"
            rel="noreferrer"
          >
            https://stripe.com/en-gi/legal/privacy-center
          </a>
        </p>
      </Section>

      <Section title="7. E-Mail-Kommunikation">
        <p>
          Wir verarbeiten E-Mail- und Kommunikationsdaten, um Eingangsbestätigungen,
          Fragebogenbestätigungen, Zugangs- oder Vertragsinformationen sowie sonstige
          vertragsbezogene Nachrichten zu versenden. Hierbei können Inhalt, Empfängeradresse,
          Versandzeitpunkt und Zustellstatus gespeichert werden.
        </p>
        <p>
          Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO sowie Art. 6 Abs. 1 lit. f DSGVO für eine
          verlässliche geschäftliche Kommunikation.
        </p>
      </Section>

      <Section title="8. Cookies, Local Storage und technisch erforderliche Speicherungen">
        <p>
          Diese Website verwendet technisch erforderliche Speicherungen, um gewünschte Funktionen
          bereitzustellen und Ihre Auswahl im Cookie-Banner zu speichern. Darüber hinaus wird
          Google Analytics 4 nur nach Ihrer ausdrücklichen Einwilligung aktiviert.
        </p>
        <ul className="space-y-2 pl-5">
          <li className="list-disc">
            Cookie zur Zwischenspeicherung des Fragebogens (`mutzur_fragebogen_v1`)
          </li>
          <li className="list-disc">
            HttpOnly-Cookie für freigeschaltete Entwurfsansichten (`mutzur_draft_preview`)
          </li>
          <li className="list-disc">
            Local-Storage-Einträge für Login- und Sitzungsdaten im Kundenbereich
          </li>
          <li className="list-disc">
            Local-Storage-Eintrag zur Speicherung Ihrer Cookie-Auswahl (`mutzur_cookie_consent_v1`)
          </li>
        </ul>
        <p>
          Rechtsgrundlage für die anschließende Verarbeitung personenbezogener Daten ist
          Art. 6 Abs. 1 lit. b bzw. lit. f DSGVO. Soweit Informationen auf Ihrem Endgerät
          gespeichert oder ausgelesen werden, erfolgt dies auf Grundlage von § 25 Abs. 2 Nr. 2
          TDDDG, soweit die Speicherung für die ausdrücklich gewünschte Funktion technisch
          erforderlich ist.
        </p>
      </Section>

      <Section title="9. Webanalyse mit Google Analytics 4">
        <p>
          Sofern Sie im Cookie-Banner ausdrücklich einwilligen, setzen wir Google Analytics 4
          ein, um Reichweite, Nutzung und Interaktionen auf unserer Website statistisch auszuwerten.
          Vor Ihrer Einwilligung bleibt Google Analytics deaktiviert und das zugehörige Skript
          wird nicht geladen.
        </p>
        <p>
          Im Rahmen der Webanalyse können insbesondere Seitenaufrufe, technische Angaben zu
          Browser und Endgerät, Referrer-Informationen sowie ungefähre Standortdaten verarbeitet
          werden. Die Verarbeitung erfolgt ausschließlich auf Grundlage Ihrer Einwilligung nach
          Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1 TDDDG. Eine erteilte Einwilligung können Sie
          jederzeit mit Wirkung für die Zukunft über den Link "Cookie-Einstellungen" im Footer
          widerrufen.
        </p>
        <p>
          Anbieter des Dienstes ist Google. Es kann dabei nicht ausgeschlossen werden, dass
          personenbezogene Daten auch an Empfänger in den USA übermittelt werden. Wenn Sie Ihre
          Einwilligung widerrufen oder ablehnen, wird die Webanalyse deaktiviert; bereits gesetzte
          Analytics-Cookies werden auf Ihrem Gerät im Rahmen der technischen Möglichkeiten gelöscht.
        </p>
      </Section>

      <Section title="10. Empfänger von Daten">
        <p>
          Personenbezogene Daten werden nur weitergegeben, soweit dies zur Bereitstellung der
          Website, zur Kommunikation, zur Vertragsdurchführung oder aufgrund gesetzlicher Pflichten
          erforderlich ist. Empfänger können insbesondere sein:
        </p>
        <ul className="space-y-2 pl-5">
          <li className="list-disc">Hosting- und Infrastruktur-Dienstleister</li>
          <li className="list-disc">Google als Anbieter von Google Analytics 4, sofern Sie einwilligen</li>
          <li className="list-disc">Zahlungsdienstleister Stripe</li>
          <li className="list-disc">E-Mail- bzw. SMTP-Dienstleister</li>
          <li className="list-disc">Steuerberater, Banken oder sonstige gesetzlich einzubeziehende Stellen</li>
        </ul>
      </Section>

      <Section title="11. Speicherdauer">
        <p>
          Wir speichern personenbezogene Daten grundsätzlich nur so lange, wie dies für die
          jeweiligen Zwecke erforderlich ist. Soweit gesetzliche Aufbewahrungspflichten bestehen,
          erfolgt die Speicherung für die Dauer dieser Fristen. Vertrags- und abrechnungsbezogene
          Daten werden daher regelmäßig bis zum Ablauf handels- und steuerrechtlicher
          Aufbewahrungsfristen gespeichert.
        </p>
        <p>
          Browserseitige Speicherungen bleiben grundsätzlich bestehen, bis Sie diese selbst löschen
          oder die technische Laufzeit abläuft.
        </p>
      </Section>

      <Section title="12. Ihre Rechte">
        <p>
          Sie haben nach Maßgabe der gesetzlichen Voraussetzungen das Recht auf Auskunft, Berichtigung,
          Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit sowie Widerspruch gegen
          Verarbeitungen, die auf Art. 6 Abs. 1 lit. e oder lit. f DSGVO beruhen.
        </p>
        <p>
          Sofern eine Verarbeitung auf einer Einwilligung beruht, können Sie diese jederzeit mit
          Wirkung für die Zukunft widerrufen. Zur Ausübung Ihrer Rechte genügt eine Nachricht an
          {` ${legalProfile.email}`}.
        </p>
      </Section>

      <Section title="13. Beschwerderecht">
        <p>
          Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren,
          insbesondere in dem Mitgliedstaat Ihres gewöhnlichen Aufenthalts, Ihres Arbeitsplatzes
          oder des Orts des mutmaßlichen Verstoßes.
        </p>
      </Section>

      <Section title="14. Änderungen dieser Datenschutzerklärung">
        <p>
          Wir passen diese Datenschutzerklärung an, wenn dies aufgrund neuer Funktionen,
          geänderter Datenverarbeitungen oder geänderter Rechtslage erforderlich wird.
        </p>
      </Section>
    </LegalPage>
  );
}
